import React, {
 useCallback, useMemo, useState, useEffect, useRef
} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EStatus } from '@common/definitions';
import { SelectInputHookForm } from '@common/components/form/inputs/select';
import Spinner from '@common/components/spinner';
import { Course } from '@modules/learning/types/objects';
import getStatus from '@modules/learning/utils/get-status';

type CourseStatusProps = {
  course: Course;
  handleStatusChange: (published: boolean, archived?: boolean) => void;
};

const CourseStatus = ({ course, handleStatusChange }: CourseStatusProps) => {
  const { t } = useTranslation();
  const [updating, setUpdating] = useState<boolean>(false);
  const isInitialMount = useRef(true);
  const previousStatusRef = useRef<EStatus | null>(null);

  const statusOptions = useMemo(() => [
    { value: EStatus.DRAFT, label: t('common:status_draft') },
    { value: EStatus.ARCHIVED, label: t('common:status_archived') },
    { value: EStatus.LIVE, label: t('common:status_live') },
  ], [t]);

  const defaultValues = useMemo(() => ({
    status: getStatus(course),
  }), [course]);

  const { control, reset } = useForm({
    mode: 'all',
    defaultValues,
  });

  const status = useWatch({
    control,
    name: 'status',
  });

  useEffect(() => {
    reset(defaultValues);
    isInitialMount.current = true;
    previousStatusRef.current = null;
  }, [course, reset, defaultValues]);

  const updateStatus = useCallback(async (newStatus: EStatus) => {
    if (getStatus(course) === newStatus) return;
    setUpdating(true);
    if (newStatus === EStatus.LIVE) {
      await handleStatusChange(true, false);
    } else {
      await handleStatusChange(false, newStatus === EStatus.ARCHIVED);
    }
    setUpdating(false);
  }, [handleStatusChange, course]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousStatusRef.current = status as EStatus;
      return;
    }

    if (status !== undefined && status !== previousStatusRef.current) {
      previousStatusRef.current = status as EStatus;
      updateStatus(status as EStatus);
    }
  }, [status, updateStatus]);

  return (
    <div className="tw-flex tw-flex-row tw-gap-2 tw-relative">
      <SelectInputHookForm
        name="status"
        control={control}
        clearable={false}
        className="FormStatusSelector"
        options={statusOptions}
        disabled={updating}
      />
      {updating && <Spinner className="tw-absolute tw-top-0 tw-right-10" />}
    </div>
  );
};

export default CourseStatus;
